import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PetContextProvider } from "./context/PetContext";
import { PublicAccessProvider } from "./context/PublicAccessContext";
import { PrivacyProvider, CookieConsentBanner, ScriptBlocker } from "./components/PrivacyConsent";
import { LanguageProvider } from "./context/LanguageContext";
import { ClubSettingsProvider } from "./context/ClubSettingsContext";
import { FeatureToggleProvider } from "./context/FeatureToggleContext";
import { ClubBrandingProvider } from "./context/ClubBrandingContext";
import { GuestIdentityProvider } from "./context/GuestIdentityContext";
import { BetaGate } from "./components/BetaGate";
import { Loader2 } from "lucide-react";
import './i18n';

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// Pages
import Landing from "./pages/Landing";
import { Login, Register } from "./pages/Auth";
import AutoLogin from "./pages/AutoLogin";
import VerifyEmail from "./pages/VerifyEmail";
import Feed from "./pages/Feed";
import Forums from "./pages/Forums";
import Catalog from "./pages/Catalog";
import Events from "./pages/Events";
import Marketplace from "./pages/Marketplace";
import Groups from "./pages/Groups";
import Profile from "./pages/Profile";
import Messages from "./pages/Messages";
import PostDetail from "./pages/PostDetail";
import Settings from "./pages/Settings";
import Files from "./pages/Files";
import ShopDetail from "./pages/ShopDetail";
import ItemDetail from "./pages/ItemDetail";
import Rankings from "./pages/Rankings";
import Breeds from "./pages/Breeds";
import Vendors from "./pages/Vendors";
import Interests from "./pages/Interests";
import Privacy from "./pages/Privacy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Friends from "./pages/Friends";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import DogParks from "./pages/DogParks";
import DogParkDetail from "./pages/DogParkDetail";
import MyPets from "./pages/MyPets";
import DogHealth from "./pages/DogHealth";
import ResearchOpportunities from "./pages/ResearchOpportunities";
import CareTeam from "./pages/CareTeam";
import Organizations from "./pages/Organizations";
import PartnerDeals from "./pages/PartnerDeals";
import GeneticHealthChecklist from "./pages/GeneticHealthChecklist";
import AdminSettings from "./pages/AdminSettings";
import FAQ from "./pages/FAQ";
import ModerationPanel from "./pages/ModerationPanel";
import BlockedUsers from "./pages/BlockedUsers";
import BannerManagement from "./pages/BannerManagement";
import PartnerPromoPage from "./pages/PartnerPromoPage";
import Recommendations from "./pages/Recommendations";
import Elections from "./pages/Elections";
import PrivateForums from "./pages/PrivateForums";
import ExploreMap from "./pages/MapView";
import FeatureRoadmap from "./pages/FeatureRoadmap";
import MedicalRecords from "./pages/MedicalRecords";
import PetFriends from "./pages/PetFriends";
import Matchmaking from "./pages/Matchmaking";
import FamilyTree from "./pages/FamilyTree";
import JobBoard from "./pages/JobBoard";
import MemberPromoPage from "./pages/MemberPromoPage";
import PromoPageEditor from "./pages/PromoPageEditor";
import DigitalContent from "./pages/DigitalContent";
import ConsumerReports from "./pages/ConsumerReports";
import MediaAlbums from "./pages/MediaAlbums";
import HealthDetection from "./pages/HealthDetection";
import AllergenGenetics from "./pages/AllergenGenetics";
import SportsEvents from "./pages/SportsEvents";
import CostRoadmap from "./pages/CostRoadmap";
import BusinessFinancials from "./pages/BusinessFinancials";
import ExternalSourcesAdmin from "./pages/ExternalSourcesAdmin";
import AdSystemConfig from "./pages/AdSystemConfig";
import LaunchChecklist from "./pages/LaunchChecklist";
import SearchPage from "./pages/Search";
import SocialChannels from "./pages/SocialChannels";
import PaymentSuccess from "./pages/PaymentSuccess";
import IdentityComplete from "./pages/IdentityComplete";
import OperatorDashboard from "./pages/OperatorDashboard";
import ClubDashboard from "./pages/ClubDashboard";
import Membership from "./pages/Membership";
import MembershipUpgrade from "./pages/MembershipUpgrade";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import FeedDirectory from "./pages/FeedDirectory";
import BlogAdminDashboard from "./pages/BlogAdminDashboard";
import ClubResources from "./pages/ClubResources";
import ClubHomepage from "./pages/ClubHomepage";
import FeedSourcesAdmin from "./pages/FeedSourcesAdmin";
import EventSourcesAdmin from "./pages/EventSourcesAdmin";
import NewsSourcesAdmin from "./pages/NewsSourcesAdmin";
import UserSubmissions from "./pages/UserSubmissions";
import SubmissionAdminDashboard from "./pages/SubmissionAdminDashboard";
import SysadminDashboard from "./pages/SysadminDashboard";
import ClubAdminDashboard from "./pages/ClubAdminDashboard";
import ClubCreatorDashboard from "./pages/ClubCreatorDashboard";
import CreateClub from "./pages/CreateClub";
import BetaFeedbackList from "./pages/BetaFeedbackList";
import BetaTestPlan from "./pages/BetaTestPlan";
import BetaAgreementPage from "./pages/BetaAgreementPage";
import PatronsPage from "./pages/PatronsPage";
import Changelog from "./pages/Changelog";
import { BetaFeedbackButton } from "./components/BetaFeedbackButton";
import AboutPage from "./pages/AboutPage";
import CreditsPage from "./pages/CreditsPage";
import ClubBlog from "./pages/ClubBlog";
import ClubRequests from "./pages/ClubRequests";
import FeaturesPage from "./pages/FeaturesPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VersionBadge from "./components/VersionBadge";
import ServiceAgreementPage from "./pages/ServiceAgreementPage";
import BetaTesterAgreement from "./pages/BetaTesterAgreement";
import ClubStoryPage from "./pages/ClubStoryPage";
import VBBOperationsCenter from "./pages/VBBOperationsCenter";
import ClubDirectory from "./pages/ClubDirectory";
import SubmitServiceProvider from "./pages/SubmitServiceProvider";
import SubmitDogPark from "./pages/SubmitDogPark";
import TestCaptcha from "./pages/TestCaptcha";
import SubGroups from "./pages/SubGroups";
import SubGroupDetail from "./pages/SubGroupDetail";
import CreateSubGroup from "./pages/CreateSubGroup";
import SuggestClub from "./pages/SuggestClub";
import FuturePlansPage from "./pages/FuturePlansPage";
import ContactPage from "./pages/ContactPage";
import BylawsPage from "./pages/BylawsPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import WorldPetsLanding from "./pages/WorldPetsLanding";
import ClubNetworkLanding from "./pages/ClubNetworkLanding";
import MemberDashboard from "./pages/MemberDashboard";
import AdminSetup from "./pages/AdminSetup";

// Lazy load less critical pages
const AdoptionAgencies = React.lazy(() => import("./pages/AdoptionAgencies"));
const AdoptablePets = React.lazy(() => import("./pages/AdoptablePets"));
const RedeemCard = React.lazy(() => import("./pages/RedeemCard"));
const Videos = React.lazy(() => import("./pages/Videos"));
const AdoptionJourney = React.lazy(() => import("./pages/AdoptionJourney"));
const Memorial = React.lazy(() => import("./pages/Memorial"));
const HallOfFame = React.lazy(() => import("./pages/HallOfFame"));
const PromoCodesAdmin = React.lazy(() => import("./pages/PromoCodesAdmin"));
const Referrals = React.lazy(() => import("./pages/Referrals"));
const ServiceSubmissionsAdmin = React.lazy(() => import("./pages/Admin/ServiceSubmissionsAdmin"));
const NGOVerificationAdmin = React.lazy(() => import("./pages/Admin/NGOVerificationAdmin"));
const PetCalendar = React.lazy(() => import("./pages/PetCalendar"));

import { ProtectedRoute, VBBProtectedRoute, Layout, useDomainContext } from './routes/AppHelpers';

function AppRoutes() {
  const { user } = useAuth();
  const { domainContext, domainLoading } = useDomainContext();

  // Determine what to show at the root path based on domain
  const RootPage = () => {
    // Show loading state while looking up custom domain
    if (domainLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    // Club-specific landing (currently only AADC has a rich landing)
    if (domainContext.type === 'club') {
      if (domainContext.club === 'all-american-dog-club') {
        return <Landing />;
      }
      if (domainContext.club === 'worldpets') {
        return <WorldPetsLanding />;
      }
      // For other clubs, show their club homepage
      // This will be expanded when more clubs have custom landings
      return <ClubHomepage />;
    }
    
    // Default: Neutral network landing for preview and unknown domains
    // This directs users to discover clubs rather than defaulting to one
    return <ClubNetworkLanding />;
  };

  return (
    <ClubBrandingProvider clubSlug={domainContext.club}>
    <FeatureToggleProvider clubSlug={domainContext.club}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <Layout showNav={true}>
          <RootPage />
        </Layout>
      } />
      {/* All-American Dog Club - full featured club site (also accessible via direct path) */}
      <Route path="/club/all-american-dog-club" element={
        <Layout showNav={true}>
          <Landing />
        </Layout>
      } />
      {/* WorldPets Global - accessible as a peer club */}
      <Route path="/club/worldpets" element={
        <Layout showNav={true}>
          <WorldPetsLanding />
        </Layout>
      } />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auto-login" element={<AutoLogin />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={
        <Layout>
          <Privacy />
        </Layout>
      } />
      <Route path="/about" element={
        <Layout>
          <AboutPage />
        </Layout>
      } />
      <Route path="/about/story" element={
        <Layout>
          <ClubStoryPage />
        </Layout>
      } />
      <Route path="/about/future" element={
        <Layout>
          <FuturePlansPage />
        </Layout>
      } />
      <Route path="/contact" element={
        <Layout>
          <ContactPage />
        </Layout>
      } />
      <Route path="/about/bylaws" element={
        <Layout>
          <BylawsPage />
        </Layout>
      } />
      <Route path="/disclaimer" element={
        <Layout>
          <DisclaimerPage />
        </Layout>
      } />
      <Route path="/about/service-agreement" element={
        <Layout>
          <ServiceAgreementPage />
        </Layout>
      } />
      <Route path="/about/beta-agreement" element={
        <Layout>
          <BetaTesterAgreement />
        </Layout>
      } />
      <Route path="/privacy-policy" element={
        <Layout>
          <PrivacyPolicy />
        </Layout>
      } />
      <Route path="/credits" element={
        <Layout>
          <CreditsPage />
        </Layout>
      } />
      <Route path="/features" element={
        <Layout>
          <FeaturesPage />
        </Layout>
      } />
      <Route path="/club-blog" element={
        <Layout>
          <ClubBlog />
        </Layout>
      } />
      <Route path="/club-blog/:postId" element={
        <Layout>
          <ClubBlog />
        </Layout>
      } />
      <Route path="/request-club" element={
        <Layout>
          <ClubRequests />
        </Layout>
      } />
      <Route path="/test-captcha" element={
        <Layout>
          <TestCaptcha />
        </Layout>
      } />
      <Route path="/admin/setup" element={
        <Layout>
          <AdminSetup />
        </Layout>
      } />
      <Route path="/clubs" element={
        <Layout>
          <ClubDirectory />
        </Layout>
      } />
      <Route path="/suggest-club" element={
        <ProtectedRoute>
          <Layout>
            <SuggestClub />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Member Dashboard - personal home for logged-in users */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <MemberDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Semi-Protected Routes (viewable but actions require auth) */}
      <Route path="/forums" element={
        <Layout>
          <Forums />
        </Layout>
      } />
      <Route path="/catalog" element={
        <Layout>
          <Catalog />
        </Layout>
      } />
      <Route path="/events" element={
        <Layout>
          <Events />
        </Layout>
      } />
      <Route path="/marketplace" element={
        <Layout>
          <Marketplace />
        </Layout>
      } />
      <Route path="/marketplace/:itemId" element={
        <Layout>
          <ItemDetail />
        </Layout>
      } />
      <Route path="/shop/:shopId" element={
        <Layout>
          <ShopDetail />
        </Layout>
      } />
      <Route path="/groups" element={
        <Layout>
          <Groups />
        </Layout>
      } />
      <Route path="/communities" element={
        <Layout>
          <SubGroups />
        </Layout>
      } />
      <Route path="/communities/create" element={
        <ProtectedRoute>
          <Layout>
            <CreateSubGroup />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/communities/:subgroupId" element={
        <Layout>
          <SubGroupDetail />
        </Layout>
      } />
      <Route path="/post/:postId" element={
        <Layout>
          <PostDetail />
        </Layout>
      } />
      <Route path="/profile/:username" element={
        <Layout>
          <Profile />
        </Layout>
      } />
      <Route path="/membership" element={
        <Layout>
          <Membership />
        </Layout>
      } />
      <Route path="/membership/upgrade" element={
        <ProtectedRoute>
          <Layout>
            <MembershipUpgrade />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/blog" element={
        <Layout>
          <Blog />
        </Layout>
      } />
      <Route path="/blog/:postId" element={
        <Layout>
          <BlogPost />
        </Layout>
      } />
      <Route path="/feeds" element={
        <Layout>
          <FeedDirectory />
        </Layout>
      } />
      <Route path="/blog/admin" element={
        <ProtectedRoute>
          <BlogAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/blog" element={
        <ProtectedRoute>
          <BlogAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/feed-sources" element={
        <ProtectedRoute>
          <FeedSourcesAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/event-sources" element={
        <ProtectedRoute>
          <EventSourcesAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/news-sources" element={
        <ProtectedRoute>
          <NewsSourcesAdmin />
        </ProtectedRoute>
      } />
      <Route path="/club/:slug/admin" element={
        <ProtectedRoute>
          <FeedSourcesAdmin />
        </ProtectedRoute>
      } />
      <Route path="/club/:slug/admin/sources" element={
        <ProtectedRoute>
          <FeedSourcesAdmin />
        </ProtectedRoute>
      } />
      <Route path="/club/:slug/admin/submissions" element={
        <ProtectedRoute>
          <Layout>
            <SubmissionAdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/sysadmin" element={
        <ProtectedRoute>
          <Layout>
            <SysadminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/beta-feedback" element={
        <ProtectedRoute>
          <Layout>
            <BetaFeedbackList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/beta-test-plan" element={
        <ProtectedRoute>
          <Layout>
            <BetaTestPlan />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/beta-agreement" element={
        <Layout>
          <BetaAgreementPage />
        </Layout>
      } />
      <Route path="/patrons" element={
        <Layout>
          <PatronsPage />
        </Layout>
      } />
      <Route path="/changelog" element={
        <Navigate to="/beta-feedback?tab=changelog" replace />
      } />
      <Route path="/club-resources" element={
        <ProtectedRoute>
          <Layout>
            <ClubResources />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/club/:slug" element={
        <Layout showNav={true}>
          <ClubHomepage />
        </Layout>
      } />
      <Route path="/vbb" element={
        <VBBProtectedRoute>
          <Layout>
            <VBBOperationsCenter />
          </Layout>
        </VBBProtectedRoute>
      } />
      <Route path="/ops" element={
        <VBBProtectedRoute>
          <Layout>
            <VBBOperationsCenter />
          </Layout>
        </VBBProtectedRoute>
      } />
      <Route path="/ops/*" element={
        <VBBProtectedRoute>
          <Layout>
            <VBBOperationsCenter />
          </Layout>
        </VBBProtectedRoute>
      } />
      
      {/* Service Provider Submission */}
      <Route path="/submit-service" element={
        <ProtectedRoute>
          <Layout>
            <SubmitServiceProvider />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/submit-park" element={
        <ProtectedRoute>
          <Layout>
            <SubmitDogPark />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/feed" element={
        <ProtectedRoute>
          <Layout>
            <Feed />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout>
            <Messages />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Referrals />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/files" element={
        <ProtectedRoute>
          <Layout>
            <Files />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/rankings" element={
        <Layout>
          <Rankings />
        </Layout>
      } />
      <Route path="/breeds" element={
        <Layout>
          <Breeds />
        </Layout>
      } />
      <Route path="/vendors" element={
        <Layout>
          <Vendors />
        </Layout>
      } />
      <Route path="/interests" element={
        <ProtectedRoute>
          <Layout>
            <Interests />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/friends" element={
        <ProtectedRoute>
          <Layout>
            <Friends />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/services" element={
        <Layout>
          <Services />
        </Layout>
      } />
      <Route path="/services/:serviceId" element={
        <Layout>
          <ServiceDetail />
        </Layout>
      } />
      <Route path="/parks" element={
        <Layout>
          <DogParks />
        </Layout>
      } />
      <Route path="/parks/:parkId" element={
        <Layout>
          <DogParkDetail />
        </Layout>
      } />
      <Route path="/my-pets" element={
        <ProtectedRoute>
          <Layout>
            <MyPets />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/health" element={
        <Layout>
          <DogHealth />
        </Layout>
      } />
      <Route path="/research" element={
        <Layout>
          <ResearchOpportunities />
        </Layout>
      } />
      <Route path="/care-team" element={
        <ProtectedRoute>
          <Layout>
            <CareTeam />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout pageTitle="Pet Calendar">
            <Suspense fallback={<PageLoader />}>
              <PetCalendar />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/organizations" element={
        <Layout>
          <Organizations />
        </Layout>
      } />
      <Route path="/deals" element={
        <Layout>
          <PartnerDeals />
        </Layout>
      } />
      <Route path="/genetic-health/:petId" element={
        <ProtectedRoute>
          <Layout>
            <GeneticHealthChecklist />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout>
            <AdminSettings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/club-admin" element={
        <ProtectedRoute>
          <Layout>
            <ClubAdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/creator-dashboard" element={
        <ProtectedRoute>
          <Layout>
            <ClubCreatorDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/clubs/create" element={
        <ProtectedRoute>
          <Layout>
            <CreateClub />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/faq" element={
        <Layout>
          <FAQ />
        </Layout>
      } />
      <Route path="/moderation" element={
        <ProtectedRoute>
          <Layout>
            <ModerationPanel />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/blocked" element={
        <ProtectedRoute>
          <Layout>
            <BlockedUsers />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/banners" element={
        <ProtectedRoute>
          <Layout>
            <BannerManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/sources" element={
        <ProtectedRoute>
          <Layout>
            <ExternalSourcesAdmin />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ads" element={
        <ProtectedRoute>
          <Layout>
            <AdSystemConfig />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/promo-codes" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <PromoCodesAdmin />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/service-submissions" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <ServiceSubmissionsAdmin />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ngo-verification" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <NGOVerificationAdmin />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/launch-checklist" element={
        <Layout>
          <LaunchChecklist />
        </Layout>
      } />
      <Route path="/adoption-agencies" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <AdoptionAgencies />
          </Suspense>
        </Layout>
      } />
      <Route path="/adopt" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <AdoptablePets />
          </Suspense>
        </Layout>
      } />
      <Route path="/redeem" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <RedeemCard />
          </Suspense>
        </Layout>
      } />
      <Route path="/redeem/:code" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <RedeemCard />
          </Suspense>
        </Layout>
      } />
      <Route path="/adoption-journey" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <AdoptionJourney />
          </Suspense>
        </Layout>
      } />
      <Route path="/videos" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Videos />
          </Suspense>
        </Layout>
      } />
      <Route path="/memorial" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Memorial />
          </Suspense>
        </Layout>
      } />
      <Route path="/memorial/:id" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Memorial />
          </Suspense>
        </Layout>
      } />
      <Route path="/hall-of-fame" element={
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <HallOfFame />
          </Suspense>
        </Layout>
      } />
      <Route path="/partners/:slug" element={
        <Layout>
          <PartnerPromoPage />
        </Layout>
      } />
      <Route path="/recommendations" element={
        <Layout>
          <Recommendations />
        </Layout>
      } />
      <Route path="/elections" element={
        <Layout>
          <Elections />
        </Layout>
      } />
      <Route path="/pro-forums" element={
        <ProtectedRoute>
          <Layout>
            <PrivateForums />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/map" element={
        <Layout>
          <ExploreMap />
        </Layout>
      } />
      <Route path="/roadmap" element={
        <Layout>
          <FeatureRoadmap />
        </Layout>
      } />
      <Route path="/medical-records" element={
        <ProtectedRoute>
          <Layout>
            <MedicalRecords />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pet-friends" element={
        <ProtectedRoute>
          <Layout>
            <PetFriends />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/matchmaking" element={
        <Layout>
          <Matchmaking />
        </Layout>
      } />
      <Route path="/family-tree" element={
        <ProtectedRoute>
          <Layout>
            <FamilyTree />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <Layout>
          <JobBoard />
        </Layout>
      } />
      <Route path="/members/:username" element={
        <Layout>
          <MemberPromoPage />
        </Layout>
      } />
      <Route path="/my-promo-page" element={
        <ProtectedRoute>
          <Layout>
            <PromoPageEditor />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/creative" element={
        <Layout>
          <DigitalContent />
        </Layout>
      } />
      <Route path="/products" element={
        <Layout>
          <ConsumerReports />
        </Layout>
      } />
      <Route path="/albums" element={
        <Layout>
          <MediaAlbums />
        </Layout>
      } />
      <Route path="/ai-health" element={
        <ProtectedRoute>
          <Layout>
            <HealthDetection />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/allergens" element={
        <Layout>
          <AllergenGenetics />
        </Layout>
      } />
      <Route path="/sports" element={
        <Layout>
          <SportsEvents />
        </Layout>
      } />
      <Route path="/invest" element={
        <Layout>
          <CostRoadmap />
        </Layout>
      } />
      <Route path="/financials" element={
        <Layout>
          <BusinessFinancials />
        </Layout>
      } />
      <Route path="/search" element={
        <ProtectedRoute>
          <Layout>
            <SearchPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/channels" element={
        <Layout>
          <SocialChannels />
        </Layout>
      } />
      <Route path="/submissions" element={
        <Layout>
          <UserSubmissions />
        </Layout>
      } />
      <Route path="/payment/success" element={
        <Layout>
          <PaymentSuccess />
        </Layout>
      } />
      <Route path="/identity/complete" element={
        <Layout>
          <IdentityComplete />
        </Layout>
      } />
      <Route path="/operator" element={
        <ProtectedRoute>
          <OperatorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/finances" element={
        <ProtectedRoute>
          <Layout>
            <ClubDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </FeatureToggleProvider>
    </ClubBrandingProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <PublicAccessProvider>
        <PetContextProvider>
          <PrivacyProvider>
            <LanguageProvider>
              <ClubSettingsProvider>
                <BrowserRouter>
                  <GuestIdentityProvider>
                    <ScriptBlocker />
                    <AppRoutes />
                    <CookieConsentBanner />
                    <BetaFeedbackButton />
                    <VersionBadge />
                    <Toaster position="top-right" richColors />
                  </GuestIdentityProvider>
                </BrowserRouter>
              </ClubSettingsProvider>
            </LanguageProvider>
          </PrivacyProvider>
        </PetContextProvider>
      </PublicAccessProvider>
    </AuthProvider>
  );
}

export default App;
